import React, { useEffect, useState, useContext } from "react";
import { GlobalVendorContex } from "./Firebase";
import { db } from "./globals/services";
import {
  useStackCollections,
  useSectorCollections,
  useCategoryCollections,
} from "./hooks/useCollectionData";
import MergeIcon from "./svgs/collections/MergeIcon";
import { Add, Search } from "@mui/icons-material";
import FilterIcon from "./svgs/collections/FilterIcon";
import SpSelector from "./globals/SpSelector";
import SingleCollection from "./pages/Collection_management/SingleCollection";
import PaginatedCollection from "./pages/Collection_management/PaginatedCollection";
import AddNew from "./pages/Collection_management/components/AddNew";
import MoveItemDialog from "./pages/Collection_management/components/MoveItemDialog";
import CsvImporter from "./pages/Collection_management/components/CsvImporter";
import { withFirebase } from "./Firebase";
import { arrayUnion, deleteField } from "firebase/firestore";

const ColManagement = (props) => {
  const [activeTab, setActiveTab] = useState("stacks");

  // AddNew component state
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [defaultData, setDefaultData] = useState({});
  const [currentCollection, setCurrentCollection] = useState("");
  const [selectedSector, setSelectedSector] = useState(null);
  const [techsCategories, setTechsCategories] = useState([]);

  // Move dialog state
  const [openMoveDialog, setOpenMoveDialog] = useState(false);
  const [itemToMove, setItemToMove] = useState(null);
  const [moveParentItem, setMoveParentItem] = useState(null);

  // Move main item dialog state
  const [openMoveMainDialog, setOpenMoveMainDialog] = useState(false);
  const [mainItemToMove, setMainItemToMove] = useState(null);

  // Move operation progress state
  const [moveProgress, setMoveProgress] = useState({
    isMoving: false,
    message: "",
    step: "",
    progress: 0,
  });

  // CSV Importer state
  const [openCsvImporter, setOpenCsvImporter] = useState(false);

  const {
    stackCategories,
    subCollections: stackSubCollections,
    loading: loadingStacks,
    loadingSub: loadingSubStacks,
    getTechnologiesForCategory,
    fetchMore: fetchMoreStacks,
    hasMore: hasMoreStacks,
    updateSubCollectionShowStatus: updateStackSubCollectionShowStatus,
    refresh: refreshStacks,
    setGlobalSearch: setStackGlobalSearch,
    setGlobalStatusFilter: setStackGlobalStatusFilter,
    setSubSearch: setStackSubSearch,
    setSubStatusFilter: setStackSubStatusFilter,
    searchQuery: stackSearchQuery,
    statusFilter: stackStatusFilter,
    subSearchQueries: stackSubSearchQueries,
    subStatusFilters: stackSubStatusFilters,
  } = useStackCollections();

  const {
    sectors,
    subCollections: sectorSubCollections,
    loading: loadingSectors,
    loadingSub: loadingSubSectors,
    getSubSectors,
    fetchMore: fetchMoreSectors,
    hasMore: hasMoreSectors,
    updateSubCollectionShowStatus: updateSectorSubCollectionShowStatus,
    refresh: refreshSectors,
    setGlobalSearch: setSectorGlobalSearch,
    setGlobalStatusFilter: setSectorGlobalStatusFilter,
    setSubSearch: setSectorSubSearch,
    setSubStatusFilter: setSectorSubStatusFilter,
    searchQuery: sectorSearchQuery,
    statusFilter: sectorStatusFilter,
    subSearchQueries: sectorSubSearchQueries,
    subStatusFilters: sectorSubStatusFilters,
  } = useSectorCollections();

  const {
    categories,
    subCollections: categorySubCollections,
    loading: loadingCategories,
    loadingSub: loadingSubCategories,
    getSubCollections,
    fetchMore: fetchMoreCategories,
    hasMore: hasMoreCategories,
    updateSubCollectionShowStatus: updateCategorySubCollectionShowStatus,
    refresh: refreshCategories,
    setGlobalSearch: setCategoryGlobalSearch,
    setGlobalStatusFilter: setCategoryGlobalStatusFilter,
    setSubSearch: setCategorySubSearch,
    setSubStatusFilter: setCategorySubStatusFilter,
    searchQuery: categorySearchQuery,
    statusFilter: categoryStatusFilter,
    subSearchQueries: categorySubSearchQueries,
    subStatusFilters: categorySubStatusFilters,
  } = useCategoryCollections();

  const [searchData, setSearchData] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // Selected Assessments for Each Collection
  const [selectedAssessmentNames, setSelectedAssessmentNames] = useState({
    stacks: [],
    domains: [],
    projects: [],
  });

  // Get current search and filter values based on active tab
  const getCurrentSearchQuery = () => {
    switch (activeTab) {
      case "stacks":
        return stackSearchQuery;
      case "sectors":
        return sectorSearchQuery;
      case "categories":
        return categorySearchQuery;
      default:
        return "";
    }
  };

  const getCurrentStatusFilter = () => {
    switch (activeTab) {
      case "stacks":
        return stackStatusFilter;
      case "sectors":
        return sectorStatusFilter;
      case "categories":
        return categoryStatusFilter;
      default:
        return "all";
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const searchValue = e.target.value;

    switch (activeTab) {
      case "stacks":
        setStackGlobalSearch(searchValue);
        break;
      case "sectors":
        setSectorGlobalSearch(searchValue);
        break;
      case "categories":
        setCategoryGlobalSearch(searchValue);
        break;
      default:
        break;
    }
  };

  // Handle status filter change
  const handleStatusChange = (status) => {
    switch (activeTab) {
      case "stacks":
        setStackGlobalStatusFilter(status);
        break;
      case "sectors":
        setSectorGlobalStatusFilter(status);
        break;
      case "categories":
        setCategoryGlobalStatusFilter(status);
        break;
      default:
        break;
    }
  };

  // Function to Search Assessments (keeping for backward compatibility)
  const searchAssessments = async (searchVal) => {
    const lowerCaseSearchValue = searchVal?.toLowerCase();
    const initialCapSearchValue =
      lowerCaseSearchValue?.charAt(0)?.toUpperCase() +
      lowerCaseSearchValue?.slice(1);
    const upperCaseSearchValue = searchVal?.toUpperCase();

    const convertedSearchValue = [
      initialCapSearchValue,
      lowerCaseSearchValue,
      upperCaseSearchValue,
    ];

    try {
      const searchIndex = await db
        .collection("catalog")
        .where("nameIndex", "array-contains-any", convertedSearchValue)
        .limit(10)
        .get();

      const results = searchIndex.docs.map((doc) => ({
        id: doc.id,
        ref: doc.ref,
        data: doc.data(),
      }));
      setSearchData(results);
      setShowDropdown(true);
    } catch (error) {
      console.error("Error searching assessments:", error);
    }
  };

  // Tech categories are no longer needed since we use refs directly
  // Keeping empty array for backward compatibility with child components
  useEffect(() => {
    setTechsCategories([]);
  }, [activeTab]);

  const handleFileUpload = (e, targetId) => {
    document.getElementById(targetId).click();
  };

  // Refresh data based on current tab
  const refreshData = () => {
    switch (activeTab) {
      case "stacks":
        refreshStacks();
        break;
      case "sectors":
        refreshSectors();
        break;
      case "categories":
        refreshCategories();
        break;
      default:
        break;
    }
  };

  // Handle adding new main collection item
  const handleAddNew = () => {
    setIsEdit(false);
    setDefaultData({});
    setSelectedSector(null);

    // Set the collection type based on active tab
    switch (activeTab) {
      case "stacks":
        setCurrentCollection("techStacks150r");
        break;
      case "sectors":
        setCurrentCollection("industry");
        break;
      case "categories":
        setCurrentCollection("categories");
        break;
      default:
        setCurrentCollection("");
    }

    setOpenAddDialog(true);
  };

  // Handle editing main collection item
  const handleEditMain = (item) => {
    setIsEdit(true);
    setDefaultData(item);
    setSelectedSector(null);
    // console.log("item", item);

    switch (activeTab) {
      case "stacks":
        setCurrentCollection("techStacks150r");
        break;
      case "sectors":
        setCurrentCollection("industry");
        break;
      case "categories":
        setCurrentCollection("categories");
        break;
      default:
        setCurrentCollection("");
    }

    setOpenAddDialog(true);
  };

  // Handle adding new sub-item
  const handleAddSubItem = (parentItem) => {
    // console.log("parentItem", parentItem);
    setIsEdit(false);
    setDefaultData({});

    switch (activeTab) {
      case "stacks":
        setCurrentCollection("techStacks150r");
        setSelectedSector(parentItem);
        break;
      case "sectors":
        setCurrentCollection("subSectors");
        setSelectedSector(parentItem);
        break;
      case "categories":
        setCurrentCollection("categories");
        setSelectedSector(parentItem);
        break;
      default:
        setCurrentCollection("");
    }

    setOpenAddDialog(true);
  };

  // Handle editing sub-item
  const handleEditSubItem = (item, parentItem) => {
    setIsEdit(true);
    setDefaultData(item);

    switch (activeTab) {
      case "stacks":
        setCurrentCollection("techStacks150r");
        setSelectedSector(parentItem);
        break;
      case "sectors":
        setCurrentCollection("subSectors");
        setSelectedSector(parentItem);
        break;
      case "categories":
        setCurrentCollection("categories");
        setSelectedSector(parentItem);
        break;
      default:
        setCurrentCollection("");
    }

    setOpenAddDialog(true);
  };

  const onCloseAddDialog = () => {
    setOpenAddDialog(false);
    setIsEdit(false);
    setDefaultData({});
    setSelectedSector(null);
    setCurrentCollection("");
  };

  // Handle moving sub-item
  const handleMoveSubItem = (item, parentItem) => {
    setItemToMove(item);
    setMoveParentItem(parentItem);
    setOpenMoveDialog(true);
  };

  const onCloseMoveDialog = () => {
    setOpenMoveDialog(false);
    setItemToMove(null);
    setMoveParentItem(null);
  };

  // Handle moving main item
  const handleMoveMainItem = (item) => {
    setMainItemToMove(item);
    setOpenMoveMainDialog(true);
  };

  const onCloseMoveMainDialog = () => {
    setOpenMoveMainDialog(false);
    setMainItemToMove(null);
  };

  // Move main item operation implementation
  const handleMoveMainItemOperation = async ({
    item,
    targetTab,
    targetParent,
    targetCollection,
    createNewCollection,
    newCollectionName,
    makeMasterSector,
  }) => {
    const currentTab = activeTab;

    // Initialize progress tracking
    setMoveProgress({
      isMoving: true,
      message: `Preparing to move ${item.name}...`,
      step: "Initializing",
      progress: 10,
    });

    try {
      const db = props.firebase.db;

      // Update progress before transaction
      setMoveProgress((prev) => ({
        ...prev,
        message: `Analyzing ${item.name} and its sub-items...`,
        step: "Analyzing",
        progress: 30,
      }));

      // Extract ALL data outside the transaction to avoid external dependencies
      const sourcePath = item.ref.path;
      const targetParentPath = targetParent ? targetParent.ref.path : null;
      const { ref, ...itemData } = item;
      const cleanItemData = {
        ...itemData,
        movedAt: new Date(),
        moveSource: sourcePath,
      };

      console.log(
        `Starting transaction: Moving ${item.name} from ${currentTab} to ${targetTab}`
      );
      console.log(`Source path: ${sourcePath}`);

      // Determine sub-collection name based on current tab (to fetch outside transaction)
      let preFetchedSubItems = [];
      let subCollectionNameOut;
      switch (currentTab) {
        case "stacks":
          subCollectionNameOut = "technologies";
          break;
        case "sectors":
          subCollectionNameOut = "subSectors";
          break;
        case "categories":
          subCollectionNameOut = "subcollections";
          break;
        default:
          subCollectionNameOut = null;
      }

      if (subCollectionNameOut) {
        const subSnapshot = await db
          .collection(`${sourcePath}/${subCollectionNameOut}`)
          .get();
        preFetchedSubItems = subSnapshot.docs.map((doc) => ({
          id: doc.id,
          data: doc.data(),
          path: doc.ref.path,
        }));
      }

      // Run the entire operation in a transaction for atomicity (completely isolated)
      const result = await db.runTransaction(async (transaction) => {
        // Create ALL references fresh within transaction context using extracted paths
        const sourceDocRef = db.doc(sourcePath);

        // Step 1: Determine target location and create target document
        let targetDocRef;
        let targetPath;

        if (createNewCollection && newCollectionName) {
          targetPath = targetCollection.collection;
          targetDocRef = db.collection(targetPath).doc();
        } else if (makeMasterSector) {
          targetPath = "sectors";
          targetDocRef = db.collection(targetPath).doc();
        } else if (targetParentPath) {
          switch (targetTab) {
            case "stacks":
              targetPath = targetCollection.collection;
              targetDocRef = db.collection(targetPath).doc();
              break;
            case "sectors":
              targetPath = `${targetParentPath}/industry`;
              targetDocRef = db.collection(targetPath).doc();
              break;
            case "categories":
              targetPath = targetCollection.collection;
              targetDocRef = db.collection(targetPath).doc();
              break;
            default:
              throw new Error(`Unknown target collection type: ${targetTab}`);
          }
        } else if (targetTab && targetTab !== currentTab) {
          targetPath = targetCollection.collection;
          targetDocRef = db.collection(targetPath).doc();
        } else {
          throw new Error("No target specified for move operation");
        }

        // Step 2: Create main item in target location
        const finalItemData =
          createNewCollection && newCollectionName
            ? { ...cleanItemData, name: newCollectionName }
            : cleanItemData;

        transaction.set(targetDocRef, finalItemData);

        // Step 3: Migrate sub-items if any exist
        if (preFetchedSubItems.length > 0) {
          // Determine target sub-collection name
          let targetSubCollectionName;
          switch (targetTab) {
            case "stacks":
              targetSubCollectionName = "technologies";
              break;
            case "sectors":
              targetSubCollectionName = "subSectors";
              break;
            case "categories":
              targetSubCollectionName = "subcollections";
              break;
            default:
              break;
          }

          if (targetSubCollectionName) {
            // Create all sub-items in target location
            for (let i = 0; i < preFetchedSubItems.length; i++) {
              const subItem = preFetchedSubItems[i];

              // Create fresh references using paths
              const targetSubRef = targetDocRef
                .collection(targetSubCollectionName)
                .doc();
              const sourceSubRef = db.doc(subItem.path);

              transaction.set(targetSubRef, {
                ...subItem.data,
                migratedAt: new Date(),
                originalPath: subItem.path,
              });

              // Delete original sub-item using fresh reference from path
              transaction.delete(sourceSubRef);
            }
          }
        }

        // Step 4: Delete original main item (this must be last)
        transaction.delete(sourceDocRef);

        return {
          success: true,
          targetPath: targetDocRef.path,
          subItemsCount: preFetchedSubItems.length,
          message: `Successfully moved ${item.name}${
            preFetchedSubItems.length > 0
              ? ` with ${preFetchedSubItems.length} sub-items`
              : ""
          }`,
        };
      });

      // Update progress after transaction completion
      setMoveProgress((prev) => ({
        ...prev,
        message:
          result.subItemsCount > 0
            ? `Successfully moved ${item.name} with ${result.subItemsCount} sub-items!`
            : `Successfully moved ${item.name}!`,
        step: "Complete",
        progress: 100,
      }));

      console.log(`Transaction completed: ${result.message}`);

      // Refresh data after successful move
      setTimeout(() => {
        // Refresh source (current) tab data first
        refreshData();

        // If we moved to a different tab, refresh that tab as well so UI is up-to-date when user switches
        if (targetTab !== currentTab) {
          switch (targetTab) {
            case "stacks":
              refreshStacks();
              break;
            case "sectors":
              refreshSectors();
              break;
            case "categories":
              refreshCategories();
              break;
            default:
              break;
          }
        }

        setMoveProgress({
          isMoving: false,
          message: "",
          step: "",
          progress: 0,
        });
      }, 1500);

      return result;
    } catch (error) {
      console.error("Transaction failed:", error);

      setMoveProgress((prev) => ({
        ...prev,
        message: `Failed to move ${item.name}: ${error.message}`,
        step: "Error",
        progress: 0,
      }));

      // Clear error message after delay
      setTimeout(() => {
        setMoveProgress({
          isMoving: false,
          message: "",
          step: "",
          progress: 0,
        });
      }, 3000);

      // Provide specific error messages
      if (error.message.includes("No target specified")) {
        throw new Error(`Move failed: No valid target location specified`);
      } else if (error.code === "permission-denied") {
        throw new Error(
          `Move failed: Permission denied. Please check your access rights.`
        );
      } else if (error.code === "unavailable") {
        throw new Error(
          `Move failed: Service temporarily unavailable. Please try again.`
        );
      } else {
        throw new Error(
          `Move failed: ${
            error.message || "Unknown error occurred. Please try again."
          }`
        );
      }
    }
  };

  // Move operation implementation
  const handleMoveItem = async ({
    item,
    targetTab,
    targetParent,
    targetCollection,
    // NEW: Parameters for creating new parent items
    createNewParent,
    newParentName,
    newParentDescription,
    createNewSector,
    newSectorName,
    newSectorDescription,
  }) => {
    try {
      const db = props.firebase.db;
      let actualTargetParent = targetParent;

      // Step 1: Create new parent item if requested
      if (createNewParent && newParentName) {
        console.log(`Creating new parent: ${newParentName} in ${targetTab}`);

        const parentData = {
          name: newParentName,
          title: newParentName,
          show: true,
          addedOn: new Date(),
          rank: 0,
          showRank: 0,
          description:
            newParentDescription ||
            `Auto-created ${
              targetTab === "sectors" ? "industry" : "parent collection"
            } for move operation`,
        };

        let newParentRef;

        if (targetTab === "sectors") {
          // For sectors, we might need to create a new sector first
          if (createNewSector && newSectorName) {
            console.log(`Creating new sector: ${newSectorName}`);

            const sectorData = {
              name: newSectorName,
              title: newSectorName,
              show: true,
              addedOn: new Date(),
              rank: 0,
              showRank: 0,
              description:
                newSectorDescription ||
                `Auto-created sector for move operation`,
            };

            // Create the new sector
            const newSectorRef = await props.firebase.createDocumentFromPath(
              "sectors",
              sectorData
            );

            // Create the industry under the new sector
            const industryPath = `${newSectorRef.path}/industry`;
            newParentRef = await props.firebase.createDocumentFromPath(
              industryPath,
              parentData
            );
          } else {
            // If no new sector, we need to find an existing sector to place the industry
            // For now, let's require sector selection for industry creation
            throw new Error(
              "When creating a new industry, you must either select an existing sector or create a new one"
            );
          }
        } else {
          // For stacks and categories, create directly in the main collection
          const collectionPath = targetCollection.collection;
          newParentRef = await props.firebase.createDocumentFromPath(
            collectionPath,
            parentData
          );
        }

        // Set the newly created parent as the target
        actualTargetParent = {
          id: newParentRef.id,
          name: newParentName,
          ref: newParentRef,
          data: parentData,
        };

        console.log(`Created new parent: ${newParentRef.path}`);
      }

      // Step 2: Extract item data before transaction
      const { ref, ...itemData } = item;
      const cleanItemData = {
        ...itemData,
        movedAt: new Date(), // Add timestamp for move tracking
      };

      // Move the item atomically using transaction
      await db.runTransaction(async (transaction) => {
        // Determine target subcollection path
        let targetPath;
        switch (targetTab) {
          case "stacks":
            targetPath = `${actualTargetParent.ref.path}/technologies`;
            break;
          case "sectors":
            // For sectors, place sub-items INSIDE the industry as subSectors
            targetPath = `${actualTargetParent.ref.path}/subSectors`;
            break;
          case "categories":
            targetPath = `${actualTargetParent.ref.path}/subcollections`;
            break;
          default:
            throw new Error(`Unknown target collection type: ${targetTab}`);
        }

        // Create fresh references within transaction
        const sourceRef = db.doc(ref.path);
        const targetRef = db.collection(targetPath).doc();

        // Create new document in target location
        transaction.set(targetRef, cleanItemData);

        // Handle assessment references update within transaction
        if (itemData.assessments && itemData.assessments.length > 0) {
          // Update all assessment documents to replace old ref with new ref
          itemData.assessments.forEach((assessment) => {
            if (assessment.ref) {
              const assessmentRef = db.doc(assessment.ref.path);
              transaction.update(assessmentRef, {
                skills: arrayUnion({
                  ref: targetRef,
                  rank: assessment.rank,
                }),
              });
            }
          });

          // Remove old assessment references
          itemData.assessments.forEach((assessment) => {
            if (assessment.ref) {
              const assessmentRef = db.doc(assessment.ref.path);
              // This requires reading the document first, so we'll handle it separately
              // For now, we'll do a simple approach and handle cleanup after transaction
            }
          });
        }

        // Remove old document
        transaction.delete(sourceRef);
      });

      // Clean up old assessment references in a separate transaction
      if (itemData.assessments && itemData.assessments.length > 0) {
        await db.runTransaction(async (transaction) => {
          // Get all assessment documents
          const assessmentDocs = await Promise.all(
            itemData.assessments.map((assessment) =>
              transaction.get(db.doc(assessment.ref.path))
            )
          );

          // Update each assessment to remove old skill reference
          assessmentDocs.forEach((assessmentDoc, index) => {
            const assessmentData = assessmentDoc.data();
            if (assessmentData.skills) {
              const updatedSkills = assessmentData.skills.filter(
                (skill) => skill.ref.path !== ref.path
              );

              transaction.update(db.doc(itemData.assessments[index].ref.path), {
                skills:
                  updatedSkills.length > 0 ? updatedSkills : deleteField(),
              });
            }
          });
        });
      }

      // Refresh data for current tab
      refreshData();

      // NEW: Also refresh sub-collections for the original parent to remove the moved item from UI
      if (moveParentItem && moveParentItem.ref) {
        switch (activeTab) {
          case "stacks":
            // Re-fetch technologies for the original parent
            getTechnologiesForCategory(moveParentItem.id, moveParentItem.ref);
            break;
          case "sectors":
            // Re-fetch subSectors for the original parent
            getSubSectors(moveParentItem.id, moveParentItem.ref);
            break;
          case "categories":
            // Re-fetch subcollections for the original parent
            getSubCollections(moveParentItem.id, moveParentItem.ref);
            break;
          default:
            break;
        }
      }

      // NEW: If moving to a different tab, refresh that tab's data and target parent's sub-collections
      if (targetTab !== activeTab) {
        switch (targetTab) {
          case "stacks":
            refreshStacks();
            // If target parent exists, refresh its sub-collections too
            if (actualTargetParent && actualTargetParent.ref) {
              getTechnologiesForCategory(
                actualTargetParent.id,
                actualTargetParent.ref
              );
            }
            break;
          case "sectors":
            refreshSectors();
            // For sectors, actualTargetParent is an industry, so refresh its subSectors
            if (actualTargetParent && actualTargetParent.ref) {
              getSubSectors(actualTargetParent.id, actualTargetParent.ref);
            }
            break;
          case "categories":
            refreshCategories();
            // If target parent exists, refresh its sub-collections too
            if (actualTargetParent && actualTargetParent.ref) {
              getSubCollections(actualTargetParent.id, actualTargetParent.ref);
            }
            break;
          default:
            break;
        }
      } else {
        // Same tab move: refresh target parent's sub-collections if different from source
        if (
          actualTargetParent &&
          actualTargetParent.ref &&
          actualTargetParent.id !== moveParentItem?.id
        ) {
          switch (targetTab) {
            case "stacks":
              getTechnologiesForCategory(
                actualTargetParent.id,
                actualTargetParent.ref
              );
              break;
            case "sectors":
              getSubSectors(actualTargetParent.id, actualTargetParent.ref);
              break;
            case "categories":
              getSubCollections(actualTargetParent.id, actualTargetParent.ref);
              break;
            default:
              break;
          }
        }
      }

      console.log(
        `Successfully moved ${item.name} to ${targetTab}${
          createNewParent ? ` (created new parent: ${newParentName})` : ""
        }`
      );
    } catch (error) {
      console.error("Error moving item:", error);
      throw error;
    }
  };

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  return (
    <div className="colManagement">
      <h1>Tech Stack Manager</h1>
      <div className="colManagement_header">
        <div className="colManagement_header__tabs">
          <button
            className={`colManagement_header__tabs__btn ${
              activeTab === "stacks" ? "active" : ""
            }`}
            onClick={() => setActiveTab("stacks")}
          >
            <h3>Stacks</h3>
          </button>
          <button
            className={`colManagement_header__tabs__btn ${
              activeTab === "sectors" ? "active" : ""
            }`}
            onClick={() => setActiveTab("sectors")}
          >
            <h3>Sectors</h3>
          </button>
          <button
            className={`colManagement_header__tabs__btn ${
              activeTab === "categories" ? "active" : ""
            }`}
            onClick={() => setActiveTab("categories")}
          >
            <h3>Categories</h3>
          </button>
          {/* <button
            className={`colManagement_header__tabs__btn `}
            onClick={() => {
              
            }}
          >
            <h3>Analytics</h3>
          </button> */}
        </div>
        <div className="colManagement_header__btns">
          <button>
            <MergeIcon />
            <span>Merge Categories</span>
          </button>
          <button onClick={() => setOpenCsvImporter(true)}>
            <Add />
            <span>Import CSV</span>
          </button>
          <button onClick={handleAddNew}>
            <Add />
            <span>New Request</span>
          </button>
        </div>
      </div>
      <div className="colManagement_filter">
        <div className="colManagement_filter__search">
          <Search />
          <input
            type="text"
            placeholder="Search"
            value={getCurrentSearchQuery()}
            onChange={handleSearchChange}
          />
        </div>
        <div className="colManagement_filter__status">
          <SpSelector
            className="status--dropDown"
            renderHeader={() => (
              <div className="status--dropDown--header">
                <span>
                  {statusOptions.find(
                    (option) => option.value === getCurrentStatusFilter()
                  )?.label || "Select Status"}
                </span>
                <FilterIcon />
              </div>
            )}
            showArrow={false}
            position="bottom"
          >
            {statusOptions.map((option) => (
              <div
                key={option.value}
                className="status--dropDown--option"
                onClick={() => {
                  handleStatusChange(option.value);
                  setShowDropdown(false);
                }}
              >
                {option.label}
              </div>
            ))}
          </SpSelector>
        </div>
      </div>
      <div className="colManagement_collections">
        {activeTab === "stacks" && (
          <PaginatedCollection
            items={stackCategories}
            loading={loadingStacks}
            hasMore={hasMoreStacks}
            fetchMore={fetchMoreStacks}
            type="stack"
            subCollections={stackSubCollections}
            loadingSub={loadingSubStacks}
            onExpand={getTechnologiesForCategory}
            updateSubCollectionShowStatus={updateStackSubCollectionShowStatus}
            onEditMain={handleEditMain}
            onAddSubItem={handleAddSubItem}
            onEditSubItem={handleEditSubItem}
            onMoveSubItem={handleMoveSubItem}
            onMoveMainItem={handleMoveMainItem}
            setSubSearch={setStackSubSearch}
            setSubStatusFilter={setStackSubStatusFilter}
            subSearchQueries={stackSubSearchQueries}
            subStatusFilters={stackSubStatusFilters}
            searchQuery={stackSearchQuery}
          />
        )}
        {activeTab === "sectors" && (
          <PaginatedCollection
            items={sectors}
            loading={loadingSectors}
            hasMore={hasMoreSectors}
            fetchMore={fetchMoreSectors}
            type="sector"
            subCollections={sectorSubCollections}
            loadingSub={loadingSubSectors}
            onExpand={getSubSectors}
            updateSubCollectionShowStatus={updateSectorSubCollectionShowStatus}
            onEditMain={handleEditMain}
            onAddSubItem={handleAddSubItem}
            onEditSubItem={handleEditSubItem}
            onMoveSubItem={handleMoveSubItem}
            onMoveMainItem={handleMoveMainItem}
            setSubSearch={setSectorSubSearch}
            setSubStatusFilter={setSectorSubStatusFilter}
            subSearchQueries={sectorSubSearchQueries}
            subStatusFilters={sectorSubStatusFilters}
            searchQuery={sectorSearchQuery}
          />
        )}
        {activeTab === "categories" && (
          <PaginatedCollection
            items={categories}
            loading={loadingCategories}
            hasMore={hasMoreCategories}
            fetchMore={fetchMoreCategories}
            type="category"
            subCollections={categorySubCollections}
            loadingSub={loadingSubCategories}
            onExpand={getSubCollections}
            updateSubCollectionShowStatus={
              updateCategorySubCollectionShowStatus
            }
            onEditMain={handleEditMain}
            onAddSubItem={handleAddSubItem}
            onEditSubItem={handleEditSubItem}
            onMoveSubItem={handleMoveSubItem}
            onMoveMainItem={handleMoveMainItem}
            setSubSearch={setCategorySubSearch}
            setSubStatusFilter={setCategorySubStatusFilter}
            subSearchQueries={categorySubSearchQueries}
            subStatusFilters={categorySubStatusFilters}
            searchQuery={categorySearchQuery}
          />
        )}
      </div>

      {/* AddNew Dialog */}
      <AddNew
        open={openAddDialog}
        onClose={onCloseAddDialog}
        setOpenDialog={setOpenAddDialog}
        isEdit={isEdit}
        setIsEdit={setIsEdit}
        defaultData={defaultData}
        collection={currentCollection}
        selectedSector={selectedSector}
        techsCategories={techsCategories}
        handleFileUpload={handleFileUpload}
        refreshData={refreshData}
        firebase={props.firebase}
        searchAssessments={searchAssessments}
        searchData={searchData}
        setSearchData={setSearchData}
        showDropdown={showDropdown}
        setShowDropdown={setShowDropdown}
        selectedAssessmentNames={selectedAssessmentNames}
        onToggleAssessment={() => {}} // Placeholder for assessment toggle functionality
        setAssessments={() => {}} // Placeholder for assessment setter
      />

      {/* Move Item Dialog */}
      <MoveItemDialog
        open={openMoveDialog}
        onClose={onCloseMoveDialog}
        item={itemToMove}
        firebase={props.firebase}
        onMove={handleMoveItem}
        currentTab={activeTab}
        currentParent={moveParentItem}
      />

      {/* Move Main Item Dialog */}
      <MoveItemDialog
        open={openMoveMainDialog}
        onClose={onCloseMoveMainDialog}
        item={mainItemToMove}
        firebase={props.firebase}
        onMove={handleMoveMainItemOperation}
        currentTab={activeTab}
        currentParent={null}
        isMainItem={true}
      />

      {/* CSV Importer Dialog */}
      <CsvImporter
        open={openCsvImporter}
        onClose={() => setOpenCsvImporter(false)}
        firebase={props.firebase}
        currentTab={activeTab}
        refreshData={refreshData}
      />

      {/* Move Progress Overlay */}
      {moveProgress.isMoving && (
        <div
          className="move-progress-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            color: "white",
          }}
        >
          <div
            className="move-progress-content"
            style={{
              backgroundColor: "#2c3e50",
              borderRadius: "12px",
              padding: "30px",
              maxWidth: "500px",
              width: "90%",
              textAlign: "center",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
            }}
          >
            <h3
              style={{
                marginBottom: "20px",
                color: "#ecf0f1",
                fontSize: "1.4em",
                fontWeight: "600",
              }}
            >
              {moveProgress.step === "Error"
                ? "⚠️ Move Failed"
                : "🔄 Moving Collection"}
            </h3>

            <div style={{ marginBottom: "20px" }}>
              <div
                style={{
                  backgroundColor: "#34495e",
                  borderRadius: "8px",
                  height: "8px",
                  overflow: "hidden",
                  marginBottom: "10px",
                }}
              >
                <div
                  style={{
                    backgroundColor:
                      moveProgress.step === "Error" ? "#e74c3c" : "#3498db",
                    height: "100%",
                    width: `${moveProgress.progress}%`,
                    transition: "width 0.3s ease",
                    borderRadius: "8px",
                  }}
                />
              </div>

              <p
                style={{
                  margin: "0",
                  color: "#bdc3c7",
                  fontSize: "0.9em",
                  lineHeight: "1.4",
                }}
              >
                {moveProgress.message}
              </p>

              {moveProgress.step && moveProgress.step !== "Error" && (
                <p
                  style={{
                    margin: "8px 0 0 0",
                    color: "#95a5a6",
                    fontSize: "0.8em",
                    fontStyle: "italic",
                  }}
                >
                  Step: {moveProgress.step}
                </p>
              )}
            </div>

            {moveProgress.step === "Error" ? (
              <p
                style={{
                  color: "#e74c3c",
                  fontSize: "0.85em",
                  margin: "0",
                  fontWeight: "500",
                }}
              >
                The operation has been rolled back. No changes were made.
              </p>
            ) : (
              <div
                style={{
                  color: "#f39c12",
                  fontSize: "0.85em",
                  lineHeight: "1.4",
                }}
              >
                <p style={{ margin: "0 0 5px 0", fontWeight: "600" }}>
                  ⚠️ Please do not close this window
                </p>
                <p style={{ margin: "0", color: "#ecf0f1" }}>
                  The move operation is in progress and must complete entirely.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default withFirebase(ColManagement);
